import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { entity, equals, number, query, string, truthy } from '~/prix'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
import AppLayout from '~/components/appLayout'
import useItem from '~/prix/react/hooks/item'
import md5 from 'md5'
import Handle from '~/prix/react/components/handle'
import { isEmpty } from '~/prix/utils/empty'
import useMedia from 'react-use/lib/useMedia'
import { format } from 'date-fns'
import { parseDateTime } from '~/prix/types/dateTime'
import { useKeycloak } from '~/components/keycloak'
import { isDevelopment } from '~/utils/shared/platform'
import { trackAnalyticsEvent } from '~/utils/client/analytics'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  aside {
    flex: 1;
    max-width: 325px;

    @media (max-width: 1100px) {
      max-width: 265px;
    }

    @media (max-width: 768px) {
      width: 100%;
      max-width: none;
    }

    img {
      width: 100%;
      height: 200px;
      object-fit: cover;

      @media (max-width: 768px) {
        width: 100%;
        max-height: 250px;
      }
    }

    h1 {
      font-size: 24px;
      margin-bottom: 10px;
      padding: 0 10px;
    }

    p {
      font-size: 14px;
      margin-bottom: 10px;
      padding: 0 10px;
    }

    label {
      display: flex;
      flex-direction: column;
      padding: 0 10px;
      margin-bottom: 10px;
    }
  }

  main {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background: #fff;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    overflow: hidden;

    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  }
`

type SectionOption = {
  value: string
  label: string
}

const roadmapOptions: SectionOption[] = [
  { value: 'Roteiro campeão', label: 'Roteiro campeão' },
  { value: 'Sugestão 1', label: 'Sugestão 1' },
  { value: 'Sugestão 2', label: 'Sugestão 2' },
  { value: 'Sugestão 3', label: 'Sugestão 3' },
]

const roadmapKeyByValue: Record<string, string> = {
  'Roteiro campeão': 'champion',
  'Sugestão 1': 'suggestion-1',
  'Sugestão 2': 'suggestion-2',
  'Sugestão 3': 'suggestion-3',
}
const fullStandardPortfolioSection = '__ALL__'
const standardPortfolioSections = [
  'Visão geral',
  'Gestão Empresarial',
  'Educação Empreendedora',
  'Comércio e serviços',
  'Inovação',
  'Sustentabilidade',
  'Economia criativa',
  'Cidade empreendedora',
  'Indústria',
  'Territórios empreendedores',
  'Agronegócios',
  'Liderança',
  'Energias renováveis',
]

const standardPortfolioSectionOptions: SectionOption[] = [
  { value: fullStandardPortfolioSection, label: 'Relatório completo' },
  ...standardPortfolioSections.map(section => ({ value: section, label: section })),
]

const normalizeSectionValue = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const standardPortfolioSectionsByNormalizedValue = new Map(
  standardPortfolioSections.map(section => [normalizeSectionValue(section), section]),
)

const Aside = ({
  reportResult,
  combinations,
  reportOutputResult,
  handleVariableChange,
  showRoadmapFilter,
  selectedRoadmapValue,
  handleRoadmapChange,
  showSectionFilter,
  sectionOptions,
  selectedSectionValue,
  handleSectionChange,
}: any) => {
  return (
    <aside>
      {reportResult.item?.imageUrl !== '' ? (
        <img src={reportResult.item?.imageUrl as string} />
      ) : null}
      <div>
        <h1>{reportResult.item?.title}</h1>
        <p>{reportResult.item?.description}</p>
      </div>

      {combinations ? (
        <div style={{ padding: '12px 0px 5px 0px' }}>
          {showSectionFilter ? (
            <label key='STANDARD_PORTFOLIO_SECTION'>
              Seção do relatório
              <Select
                options={sectionOptions}
                value={sectionOptions.find(
                  (option: SectionOption) => option.value === selectedSectionValue,
                )}
                onChange={option =>
                  handleSectionChange(
                    (option as SectionOption | null)?.value || fullStandardPortfolioSection,
                  )
                }
                isLoading={reportOutputResult.isLoading}
                isClearable={false}
              />
            </label>
          ) : null}
          {combinations.map(combination => {
            const allOptions = [combination.empty, ...combination.options].filter(truthy)
            const mapOption = (option: { id: number | string | null; name: string }) => ({
              value: option.id === null ? '' : String(option.id),
              label: option.name,
            })
            const options = allOptions.map(mapOption)
            const defaultValue =
              allOptions.filter(option => option.default).map(mapOption)[0] || null
            const variableValue = reportOutputResult.item?.variables
              ? (reportOutputResult.item?.variables as any)[combination.variable]
              : undefined
            const value = reportOutputResult.item?.variables
              ? allOptions
                  .filter(
                    option =>
                      (isEmpty(variableValue) ? '' : String(variableValue)) ===
                      (option.id === null ? '' : String(option.id)),
                  )
                  .map(mapOption)[0]
              : undefined

            return (
              <label key={combination.variable}>
                <>
                  {combination.name}
                  <Select
                    options={options}
                    defaultValue={defaultValue}
                    value={value}
                    onChange={option =>
                      handleVariableChange(
                        combination.variable,
                        option?.value as string,
                        option?.value === defaultValue?.value,
                      )
                    }
                    isLoading={reportOutputResult.isLoading}
                    isClearable={Boolean(combination.empty)}
                  />
                </>
              </label>
            )
          })}
          {showRoadmapFilter ? (
            <label key='AGENT_ADVISED_ROADMAP'>
              Roteiro
              <Select
                options={roadmapOptions}
                value={roadmapOptions.find(option => option.value === selectedRoadmapValue)}
                onChange={option =>
                  handleRoadmapChange(
                    (option as SectionOption | null)?.value || roadmapOptions[0].value,
                  )
                }
                isLoading={reportOutputResult.isLoading}
                isClearable={false}
              />
            </label>
          ) : null}
        </div>
      ) : null}

      {reportOutputResult.item?.updatedAt ? (
        <p>
          {' '}
          Data de atualização:{' '}
          {format(parseDateTime(reportOutputResult.item?.updatedAt), 'dd/MM/yyyy')}
        </p>
      ) : null}
    </aside>
  )
}

export default function ReportReadScreen() {
  const params = useParams()
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const mustScrollRoadmapToBottomRef = useRef<boolean>(false)
  const key = params.key as string
  const hash = params.hash
  const isAgentAdvisedRoadmapsReport = key === 'agent-advised-roadmaps'
  const [selectedRoadmapValue, setSelectedRoadmapValue] = useState<string>(roadmapOptions[0].value)
  const isStandardPortfolioReport = key === 'standard-portfolio-report'
  const [selectedSectionValue, setSelectedSectionValue] = useState<string>(
    fullStandardPortfolioSection,
  )
  const isMobile = useMedia('(max-width: 768px)')

  const auth = useKeycloak()
  const cacheSeconds = isDevelopment ? 1 : 60 * 60 * 4

  const reportResult = useItem(
    () =>
      query('report')
        .select({
          id: entity('report').property('id'),
          key: entity('report').property('key'),
          title: entity('report').property('title'),
          description: entity('report').property('description'),
          imageUrl: entity('report').property('imageUrl'),
          combinations: entity('report').property('combinations'),
          isPublic: entity('report').property('isPublic'),
        })
        .where(equals(entity('report').property('key'), string().value(key)))
        .limit(1),
    [key],
    {
      cache: cacheSeconds,
      autoLoad: true,
    },
  )

  const analyticalLibraryReportKeys = new Set([
    'standard-portfolio-report',
    'course-life-cycle',
    'course-dropout',
    'subnormal-clusters',
    'agent-advised-roadmaps',
    'standard-portfolio-report',
    'service-integrations-monitoring',
  ])
  const dockActive = analyticalLibraryReportKeys.has(key) ? 'analytical-library' : 'start'
  const reportId = reportResult.item?.id

  // Combinations
  const combinations = reportResult.item?.combinations as
    | {
        name: string
        variable: string
        options: { id: number | string; name: string; default: boolean }[]
        empty: { id: number | string | null; name: string; default: boolean }
      }[]
    | undefined

  const reportOutputResult = useItem(
    () =>
      query('reportOutput')
        .select({
          id: entity('reportOutput').property('id'),
          reportId: entity('reportOutput').property('reportId'),
          hash: entity('reportOutput').property('hash'),
          html: entity('reportOutput').property('html'),
          variables: entity('reportOutput').property('variables'),
          createdAt: entity('reportOutput').property('createdAt'),
          updatedAt: entity('reportOutput').property('updatedAt'),
        })
        .where(
          hash
            ? equals(entity('reportOutput').property('hash'), string().value(hash))
            : equals(entity('reportOutput').property('hash'), string().value('-')),
          equals(entity('reportOutput').property('reportId'), number().value(reportId as number)),
        )
        .limit(1),
    [reportId, hash, reportResult.item],
    {
      cache: cacheSeconds,
      autoLoad: Boolean(reportResult.item),
    },
  )

  const iframeUrl = useMemo(() => {
    const html = reportOutputResult.item?.html as string | undefined
    if (!html) {
      return null
    }
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    return url
  }, [reportOutputResult.item?.html])

  const handleVariableChange = (variable: string, value: string, isDefault?: boolean) => {
    trackAnalyticsEvent('feature_used', {
      feature: 'report_variable_changed',
      source_screen: 'report',
    })
    setIsLoadingReportStyle(true)
    const variables = reportOutputResult.item?.variables as any
    if (!combinations) {
      return
    }
    const newVariables = [...combinations]
      .sort((a, b) => a.variable.localeCompare(b.variable))
      .reduce((obj: any, combination) => {
        if (combination.variable === variable) {
          obj[variable] = isEmpty(value) ? null : String(value)
          return obj
        }
        if (variables && Object.prototype.hasOwnProperty.call(variables, combination.variable)) {
          obj[combination.variable] = isEmpty(variables[combination.variable])
            ? null
            : String(variables[combination.variable])
          return obj
        }
        const defaultOption = combination.options.find(option => option.default)
        if (defaultOption) {
          obj[combination.variable] = isEmpty(defaultOption.id) ? null : String(defaultOption.id)
          return obj
        }
        obj[combination.variable] = null
        return obj
      }, {})

    const newHash = !isDefault ? md5(JSON.stringify(newVariables)) : undefined
    console.debug(newHash, JSON.stringify(newVariables))
    navigate(`/app/report/${key}/${newHash ? newHash : ''}`)
  }

  const [isLoadingReportStyle, setIsLoadingReportStyle] = useState<boolean>(true)
  const handleRoadmapChange = useCallback((value: string) => {
    mustScrollRoadmapToBottomRef.current = true
    setSelectedRoadmapValue(value)
  }, [])

  const applyRoadmapFilter = useCallback(() => {
    if (!isAgentAdvisedRoadmapsReport) {
      return
    }

    const iframe = iframeRef.current
    if (!iframe || !iframe.contentWindow) {
      return
    }

    const doc = iframe.contentWindow.document
    const hiddenClass = 'agent-advised-roadmaps-hidden-by-filter'
    Array.from(doc.querySelectorAll(`.${hiddenClass}`)).forEach(element => {
      element.classList.remove(hiddenClass)
    })

    const selectedRoadmapKey = roadmapKeyByValue[selectedRoadmapValue]
    if (!selectedRoadmapKey) {
      return
    }

    const roadmapBlocks = Array.from(doc.querySelectorAll('[data-roadmap-key]')) as HTMLElement[]
    if (!roadmapBlocks.length) {
      return
    }

    let hasSelectedBlock = false
    roadmapBlocks.forEach(block => {
      const shouldShow = block.dataset.roadmapKey === selectedRoadmapKey
      if (shouldShow) {
        hasSelectedBlock = true
      } else {
        block.classList.add(hiddenClass)
      }
    })

    if (!hasSelectedBlock) {
      return
    }

    const routeMapTitle = Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6')).find(
      heading => heading.textContent?.trim() === 'Mapa com o roteiro',
    ) as HTMLElement | undefined
    const additionalRoutesTitle = Array.from(
      doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6'),
    ).find(heading => heading.textContent?.trim() === 'Sugestões adicionais de roteiros') as
      | HTMLElement
      | undefined

    if (selectedRoadmapKey === 'champion') {
      additionalRoutesTitle?.classList.add(hiddenClass)
    } else {
      routeMapTitle?.classList.add(hiddenClass)
    }

    if (!mustScrollRoadmapToBottomRef.current) {
      return
    }

    mustScrollRoadmapToBottomRef.current = false

    const scrollToBottom = () => {
      const maxScrollTop = Math.max(
        doc.documentElement?.scrollHeight || 0,
        doc.body?.scrollHeight || 0,
      )
      iframe.contentWindow?.scrollTo({ top: maxScrollTop, left: 0, behavior: 'auto' })
    }

    iframe.contentWindow.requestAnimationFrame(() => {
      scrollToBottom()
      iframe.contentWindow?.requestAnimationFrame(scrollToBottom)
    })
  }, [isAgentAdvisedRoadmapsReport, selectedRoadmapValue])

  const applyStandardPortfolioSectionFilter = useCallback(() => {
    if (!isStandardPortfolioReport) {
      return
    }

    const iframe = iframeRef.current
    if (!iframe || !iframe.contentWindow) {
      return
    }

    const doc = iframe.contentWindow.document
    const hiddenClass = 'standard-portfolio-hidden-by-filter'
    Array.from(doc.querySelectorAll(`.${hiddenClass}`)).forEach(element => {
      element.classList.remove(hiddenClass)
    })

    const cellSelectors = ['.jp-Notebook .jp-Cell', '.jp-Cell', '.cell']
    let cells: HTMLElement[] = []
    for (const selector of cellSelectors) {
      const foundCells = Array.from(doc.querySelectorAll(selector)) as HTMLElement[]
      if (foundCells.length) {
        cells = foundCells
        break
      }
    }

    if (selectedSectionValue === fullStandardPortfolioSection) {
      return
    }

    let hasSelectedSection = false

    if (cells.length) {
      let currentSection: string | null = null
      const resolveCellHeading = (cell: HTMLElement) => {
        const headingElements = Array.from(cell.querySelectorAll('h1, h2, h3, h4, h5, h6'))
        for (const headingElement of headingElements) {
          const normalizedHeading = normalizeSectionValue(headingElement.textContent || '')
          const matchedSection = standardPortfolioSectionsByNormalizedValue.get(normalizedHeading)
          if (matchedSection) {
            return matchedSection
          }
        }
        return null
      }

      const mappedCells = cells.map(cell => {
        const matchedSection = resolveCellHeading(cell)
        if (matchedSection) {
          currentSection = matchedSection
        }
        return { cell, section: currentSection }
      })

      hasSelectedSection = mappedCells.some(
        mappedCell => mappedCell.section === selectedSectionValue,
      )

      mappedCells.forEach(({ cell, section }) => {
        if (hasSelectedSection && section !== selectedSectionValue) {
          cell.classList.add(hiddenClass)
        }
      })
    }

    if (!hasSelectedSection) {
      const sectionHeadings = (
        Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6')) as HTMLElement[]
      )
        .map(heading => {
          const matchedSection = standardPortfolioSectionsByNormalizedValue.get(
            normalizeSectionValue(heading.textContent || ''),
          )
          return { heading, section: matchedSection }
        })
        .filter((item): item is { heading: HTMLElement; section: string } => Boolean(item.section))

      const selectedHeadingIndex = sectionHeadings.findIndex(
        item => item.section === selectedSectionValue,
      )

      if (selectedHeadingIndex !== -1) {
        const startHeading = sectionHeadings[selectedHeadingIndex].heading
        const nextHeading = sectionHeadings[selectedHeadingIndex + 1]?.heading
        const range = doc.createRange()
        range.setStartBefore(startHeading)
        if (nextHeading) {
          range.setEndBefore(nextHeading)
        } else {
          range.setEndAfter(doc.body.lastElementChild || startHeading)
        }

        const allElements = Array.from(doc.body.querySelectorAll('*')) as HTMLElement[]
        allElements.forEach(element => {
          if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
            return
          }
          if (!range.intersectsNode(element)) {
            element.classList.add(hiddenClass)
          }
        })
      }
    }

    iframe.contentWindow.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [isStandardPortfolioReport, selectedSectionValue])

  useEffect(() => {
    setSelectedSectionValue(fullStandardPortfolioSection)
  }, [key])

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe || !iframe.contentWindow) {
      return
    }

    const doc = iframe.contentWindow.document
    if (!doc) {
      return
    }

    setIsLoadingReportStyle(false)

    let fontElement = doc.getElementById('iframe-figtree-font') as HTMLLinkElement
    if (!fontElement) {
      fontElement = doc.createElement('link')
      fontElement.id = 'iframe-figtree-font'
      fontElement.rel = 'stylesheet'
      fontElement.href =
        'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap'
      doc.head.appendChild(fontElement)
    }

    let styleElement = doc.getElementById('iframe-style') as HTMLStyleElement
    if (!styleElement) {
      styleElement = doc.createElement('style')
      styleElement.id = 'iframe-style'
      doc.head.appendChild(styleElement)
    }

    styleElement.textContent = `
      main {
        margin-left: 20px;
      }

      body,
      .jp-RenderedHTMLCommon,
      .jp-RenderedHTMLCommon *,
      .jp-RenderedText,
      .jp-RenderedText *,
      .jp-Cell,
      .jp-Cell *,
      .jp-OutputArea,
      .jp-OutputArea * {
        font-family: 'Figtree', Arial, sans-serif;
      }

      .jp-MarkdownCell {
        max-width: 100%;
      }

      .jp-InputPrompt {
        display: none;
      }

      .jp-Cell {
        padding: 0;
      }

      .jp-OutputPrompt {
        display: none;
      }

      .jp-RenderedText pre {
        font-family: 'Figtree', Arial, sans-serif;
        font-size: 14px;
        margin: 0px 0px 7px;
        line-height: 23px;
        word-break: break-word;
        white-space: normal
        min-width: 0;
        max-width: 100%;
      }

      .jp-RenderedHTMLCommon h5 {
        font-size: 16px;
      }

      .agent-advised-roadmaps-hidden-by-filter,
      .standard-portfolio-hidden-by-filter {
        display: none !important;
      }
    `

    applyRoadmapFilter()
    applyStandardPortfolioSectionFilter()

    /* reaplica as formulas após o layout/styles do iframe 
    (necessário para o MathJax reconhecer as mudanças de estilo e não manter as formulas com um font size menor) */
    setTimeout(() => {
      const mathJax = (iframe.contentWindow as any).MathJax
      if (mathJax?.typesetPromise) {
        mathJax.typesetPromise()
      } else if (mathJax?.Hub?.Queue) {
        mathJax.Hub.Queue(['Typeset', mathJax.Hub])
      }
    }, 0)
  }, [applyRoadmapFilter, applyStandardPortfolioSectionFilter])

  useEffect(() => {
    setSelectedRoadmapValue(roadmapOptions[0].value)
  }, [key])

  useEffect(() => {
    setIsLoadingReportStyle(true)
  }, [iframeUrl])

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) {
      return
    }
    if (iframe.contentDocument?.readyState === 'complete') {
      handleIframeLoad()
    }
  }, [iframeUrl, handleIframeLoad])

  useEffect(() => {
    applyRoadmapFilter()
    applyStandardPortfolioSectionFilter()
  }, [applyStandardPortfolioSectionFilter, applyRoadmapFilter, iframeUrl])

  return (
    <AppLayout
      title='Relatório'
      dockActive={dockActive}
      mustBeAuthenticated={
        reportResult.item?.isPublic === true && auth.isAuthenticated === false ? false : true
      }
      error={reportResult.error}
      isLoading={reportResult.isLoading}
      menu={
        isMobile ? (
          <Wrapper>
            <Aside
              reportResult={reportResult}
              combinations={combinations}
              reportOutputResult={reportOutputResult}
              handleVariableChange={handleVariableChange}
              showRoadmapFilter={isAgentAdvisedRoadmapsReport}
              selectedRoadmapValue={selectedRoadmapValue}
              handleRoadmapChange={handleRoadmapChange}
              showSectionFilter={isStandardPortfolioReport}
              sectionOptions={standardPortfolioSectionOptions}
              selectedSectionValue={selectedSectionValue}
              handleSectionChange={setSelectedSectionValue}
            />
          </Wrapper>
        ) : null
      }
    >
      <Wrapper>
        {!isMobile ? (
          <Aside
            reportResult={reportResult}
            combinations={combinations}
            reportOutputResult={reportOutputResult}
            handleVariableChange={handleVariableChange}
            showRoadmapFilter={isAgentAdvisedRoadmapsReport}
            selectedRoadmapValue={selectedRoadmapValue}
            handleRoadmapChange={handleRoadmapChange}
            showSectionFilter={isStandardPortfolioReport}
            sectionOptions={standardPortfolioSectionOptions}
            selectedSectionValue={selectedSectionValue}
            handleSectionChange={setSelectedSectionValue}
          />
        ) : null}
        <main>
          <Handle error={reportOutputResult.error} isLoading={reportOutputResult.isLoading}>
            {iframeUrl ? (
              <iframe
                src={iframeUrl}
                ref={iframeRef}
                onLoad={handleIframeLoad}
                style={
                  isLoadingReportStyle === false
                    ? { display: 'block', visibility: 'visible' }
                    : { display: 'block', visibility: 'hidden' }
                }
              />
            ) : null}
          </Handle>
        </main>
      </Wrapper>
    </AppLayout>
  )
}
